import { FC, useEffect, useState } from "react";
import { Tender, Tendering } from "../../../models/tendering";
import { Button, Card, CardHeader, CardPreview, Divider, Link, Text, Title3, Tooltip } from "@fluentui/react-components";
import ContactChange from "../Dialogs/ContactChange";
import { useAppContext } from "../../../AppContext";
import { RoleDTO } from "../../../models/role";
import TenderManagerDialog from "../Dialogs/TenderManagerDialog";
import { User } from "../../../models/users";
import { getManagerUserByTenderID } from "../../../services/usersService";
import UserDrawer from "../../drawers/UserDrawer";
import { set } from "react-hook-form";

interface TenderInfoProps{
  tenderDocument: Tender;
  tenderingDocument: Tendering;
  isProcurer: boolean;
  updateTender?: (tender: Tender) => void;
  onTabChange?: (tabName: string, overviewQuestionIDs?: number) => void;
}

const TenderInfo: FC<TenderInfoProps> = (props: TenderInfoProps) => {
  const [contactChangeOpen, setContactChangeOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'email' | 'phone'>('email');
  const [overviewAnswers, setOverviewAnswers] = useState<any[]>([]);
  const [openTenderManagerDialog, setOpenTenderManagerDialog] = useState<boolean>(false);
  const [openUserDrawer, setOpenUserDrawer] = useState<boolean>(false);
  const [manager, setManager] = useState<User>();

  const {user, getFeaturePermissionsByModuleID} = useAppContext();
  const featurePermissions = getFeaturePermissionsByModuleID(props.tenderDocument.tenderID);

  const getManager = async () => {
    const result = await getManagerUserByTenderID(props.tenderDocument.tenderID);
    setManager(result);
  }

  useEffect(() => {
    configureOverviewAnswers();
    if(user.roles?.some((r: RoleDTO) => r.roleType == "General")){
      getManager();
    }
  }, [])

  const configureOverviewAnswers = () => {
    if(!props.tenderingDocument.properties.questionMappings.overviewQuestions){
      return;
    }

    let overviewQuestions = props.tenderingDocument.properties.questionMappings.overviewQuestions;

    if(isNaN(overviewQuestions)){
      const answers: any[] = [];
      overviewQuestions.forEach((id: any) => {
        answers.push({
          questionID: id,
          text: props.tenderingDocument.tenderingParts[0].questions?.find(q => q.questionID == id)?.text,
          heading: props.tenderingDocument.tenderingParts[0].questions?.find(q => q.questionID == id)?.heading,
          type: props.tenderingDocument.tenderingParts[0].questions?.find(q => q.questionID == id)?.questionType,
          answer: props.tenderDocument.tenderAnswers?.find(q => q.questionID == id)?.value
        })
      })
      setOverviewAnswers(answers);
    }
    else{
      const answers: any[] = [];
      answers.push({
        questionID: overviewQuestions,
        text: props.tenderingDocument.tenderingParts[0].questions?.find(q => q.questionID == overviewQuestions)?.text,
        heading: props.tenderingDocument.tenderingParts[0].questions?.find(q => q.questionID == overviewQuestions)?.heading,
        type: props.tenderingDocument.tenderingParts[0].questions?.find(q => q.questionID == overviewQuestions)?.questionType,
        answer: props.tenderDocument.tenderAnswers?.find(q => q.questionID == overviewQuestions)?.value
      })
      setOverviewAnswers(answers);
    }
  }

  const getQuestionValueFromMappings = (property: string): string => {
    const mapping = props.tenderingDocument.properties.questionMappings[property];
    if(mapping){
      const question = props.tenderDocument.tenderAnswers?.find(q => q.questionID === mapping);
      return question ? question.value : "";
    }
    return "";
  }

  const getOverviewText = (): string => {
    const mapping = props.tenderingDocument.properties.overviewQuestion;
    if(mapping){
      const question = props.tenderDocument.tenderAnswers?.find(q => q.questionID === mapping);
      return question ? question.value : "";
    }
    return "";
  }

  const getMapSrc = (): string => {
    if(!props.tenderingDocument.properties.questionMappings.coordinates)
      return '';

    const cords = getQuestionValueFromMappings("coordinates");
    if(cords){
      const cordsArray = cords.split(",");
      return `https://maps.google.com/maps?z=7&t=m&q=loc:${cordsArray[0]}+${cordsArray[1]}&ie=UTF8&iwloc=&output=embed`
    }
    return '';
  }

  const convertBooleanAnswer = (answer: string): string => {
    return answer == '0' ? 'Nej' : 'Ja';
  } 

  return (
  <>
    <div className="flex flex-col gap-2">
      {props.tenderingDocument.properties.overviewQuestion && <><Text weight="semibold">Om {props.tenderDocument.tenderName}</Text><div
          className="hover:bg-gray-100 cursor-pointer p-2 rounded transition-colors"
          onClick={() => props.onTabChange?.('facts', props.tenderingDocument.properties.overviewQuestion)}
          dangerouslySetInnerHTML={{
            __html: getOverviewText()?.replace(/\n/g, '<br>')
          }} /></>}
      {overviewAnswers.map((oa, index) => (
        <div key={index}>
          <Divider />
          <div className="hover:bg-gray-100 cursor-pointer p-2 rounded transition-colors">
            <Text weight="semibold">{oa.text}</Text>
            <div 
              className="hover:bg-gray-100 cursor-pointer p-2 rounded transition-colors"
              onClick={() => props.onTabChange?.('facts', oa.questionID)}
              dangerouslySetInnerHTML={{ __html: oa.type == 'Ja/Nej svar' ? convertBooleanAnswer(oa.answer) : (oa.answer ?? '').replace(/\n/g, '<br>') }}
            />
          </div>
        </div>
      ))}

    </div>
    <div className="gmap_canvas mt-10">
      <iframe
        width="100%"
        height="200"
        id="gmap_canvas"
        src={getMapSrc()}
        ></iframe>
      <br />
    </div>
    {featurePermissions?.some(fp => fp.action == 'Tender page/Edit tender contact') && <Card className="mb-5">
      <CardPreview>
        <Title3>Förfrågningar kontaktuppgifter</Title3>
      </CardPreview>
      <Text weight="semibold">E-post för mottagande av förfrågningar:</Text>
      <Link href={props.tenderDocument.contact?.email}>{props.tenderDocument.contact?.email}</Link>
      <Divider />
      <div className="flex justify-start">
        <Button appearance="primary" onClick={() => { setEditMode('email'); setContactChangeOpen(true); }}>Redigera e-post</Button>
      </div>
      <Divider />
      <Text weight="semibold">Telefonnummer för mottagande av förfrågningar på sms (endast mobilnummer):</Text>
      <Link href={`tel:${props.tenderDocument.contact?.phone}`}>{props.tenderDocument.contact?.phone}</Link>
      <Divider />
      <div className="flex justify-start">
        <Button appearance="primary" onClick={() => { setEditMode('phone'); setContactChangeOpen(true); }}>Redigera telefonnummer</Button>
      </div>
    </Card>}
    {user.roles?.some((r: RoleDTO) => r.roleType == "General") && manager && <Card className="mb-5">
      <CardPreview>
        <Title3>Föreståndare kontaktuppgifter</Title3>
      </CardPreview>
      <div className="flex flex-col gap-2">
        <Text weight="semibold">Föreståndarens e-post:</Text>
        <Link href={manager.email}>{manager.email}</Link>
        <Divider />
        <Text weight="semibold">Föreståndarens telefonnummer:</Text>
        <Link href={`tel:${manager.phoneNumber}`}>{manager.phoneNumber}</Link>
        <Divider />
      </div>
      <div className="flex justify-start gap-2">
        <Button appearance="primary" onClick={() => setOpenTenderManagerDialog(true)}>Ange ny föreståndare</Button>
        <Tooltip content="Det finns ingen föreståndare" relationship="label" visible={manager.userID == 0 ? undefined : false}>
          <Button disabled={manager.userID == 0} appearance="primary" onClick={() => setOpenUserDrawer(true)}>Redigera föreståndare</Button>
        </Tooltip>
      </div>
    </Card>}
    <ContactChange 
      tender={props.tenderDocument}
      editMode={editMode}
      open={contactChangeOpen} 
      onClose={() => setContactChangeOpen(false)}
      onSaved={props.updateTender}
    />
    <TenderManagerDialog
      open={openTenderManagerDialog}
      onClose={() => {
        setOpenTenderManagerDialog(false);
        getManager();
      }}
      moduleID={props.tenderDocument.tenderID}
    />
    {manager && manager.userID > 0 && <UserDrawer 
      open={openUserDrawer}
      onClose={() => setOpenUserDrawer(false)}
      user={manager}
      organizations={[]}
      onUserUpdated={(res) => console.log(res)}
    />}
  </>
  );
}

export default TenderInfo;